import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDashboardRouter as useRouter } from "@/hooks/useDashboardRouter";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator, CommandShortcut } from "@/components/ui/command";
import { CommandDialogContent } from "@/components/ui/command-dialog";
import { Workflow, CirclePlay, Terminal, GaugeCircleIcon, DatabaseZap, HardDrive, BarChart3, Settings, Search, FileText, HelpCircle, GitPullRequest, GitMerge, GitPullRequestClosed, GitPullRequestDraft, Building2, Sun, Moon, Monitor, Sparkles, Clock, Activity, MessageSquare, CalendarClock, Ruler } from "lucide-react";
import { SiDocker } from "@icons-pack/react-simple-icons";
import { useTheme } from "next-themes";
import { useSetPersistedTheme } from "@/hooks/useAppearanceSettings";
import { formatDistanceToNow } from "date-fns";
import { useWorkflowCache } from "@/hooks/useWorkflowCache";
import { useOrgFromPathname } from "@/hooks/useOrgFromPathname";
import { useActiveGithubOrgForUserNonSuspense } from "@/hooks/useConnectedGithubOrgs";
import { useAssistantSessionCache, searchSessions, getRecentSessions, type SearchedSession } from "@/hooks/useAssistantSessionCache";
import { useSemanticSessionSearch } from "@/hooks/useSemanticSessionSearch";
import { useAuth } from "@/components/AuthProvider";
import { useIsMac } from "@/hooks/useIsMac";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useAtom } from "jotai";
import { recentTimeTabAtom, recentDateRangeAtom } from "@/atoms/runsFilterAtoms";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { CodesmithIcon } from "@/components/icons/CodesmithIcon";
import { GithubActionsIcon } from "@/components/icons/GithubActionsIcon";
import { labelsToDateRanges } from "@/lib/utils";
import { useDateRangeQueryParams } from "@/hooks/useDateRangeQueryParams";
import { navigationItemMatchesSearch, getNavigationItemPriority as getNavigationItemPriorityFor, getActionItemPriority as getActionItemPriorityFor } from "@/components/commandPaletteMatch";
import { codesmithUsagePath, githubActionsUsagePath, settingsShortcutPath } from "@/components/commandPaletteShortcuts";
import { isCodesmithPath, settingsPagePath } from "@/components/sidebar/useActiveApp";
import { getCachedLastActiveOrg, useCodesmithEnabledWithCache } from "@/components/sidebar/useCodesmithEnabledCache";
import { settingsPath } from "@/lib/settingsRoutes";
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const router = useRouter();
  // Use pathname-based org to avoid suspense from useActiveGithubOrgForUser
  const urlOrg = useOrgFromPathname();
  const currentOrg = router.query.org as string || urlOrg;
  const {
    workflows,
    jobs
  } = useWorkflowCache();
  const {
    codesmithAutomationsEnabled,
    activeGithubOrg,
    orgsResolved
  } = useActiveGithubOrgForUserNonSuspense();
  // Cache-backed like the rail's Codesmith row: while the org query is in
  // flight (or errored) this assumes the last resolved value, so the usage
  // command is reachable before /user/github/orgs answers. Only a resolved
  // or cached false hides it.
  const codesmithEnabled = useCodesmithEnabledWithCache() !== false;
  // Prefer the URL/pathname org, but fall back to the active org resolved by
  // the connected-orgs hook (covers legacy routes like /logs that have no
  // org segment, where currentOrg would otherwise be empty/null). While orgs
  // are unresolved the cached last-active org backstops it, so navigation
  // targets use the same org the cached codesmithEnabled was read from.
  const resolvedOrg = currentOrg || activeGithubOrg || (orgsResolved ? undefined : getCachedLastActiveOrg());
  const {
    sessions: cachedSessions
  } = useAssistantSessionCache();
  // Server-side semantic search over embedded session transcripts. Only
  // fires (debounced) while the palette is open with a 3+ char query.
  const {
    sessions: semanticSessions
  } = useSemanticSessionSearch(open ? search : "");
  const {
    user
  } = useAuth();
  const {
    theme
  } = useTheme();
  const setTheme = useSetPersistedTheme();
  const [, setRecentTimeTab] = useAtom(recentTimeTabAtom);
  const [, setRecentDateRange] = useAtom(recentDateRangeAtom);
  const {
    updateDateRangeUrl
  } = useDateRangeQueryParams({
    source: "recent_range"
  });
  const isMac = useIsMac();
  const isOnCodesmithPage = isCodesmithPath(router.pathname);
  const isOnSessionPage = router.pathname.includes("/sessions") || router.pathname.includes("/assistant");
  const navigate = useCallback((path: string) => {
    router.push(path);
    setOpen(false);
    setSearch("");
  }, [router]);

  // Handle keyboard shortcut (cmd+k or ctrl+k)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Open command palette on cmd+k or ctrl+k (always, even inside inputs)
      if (event.key === "k" && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        setOpen(prev => !prev);
        return;
      }
      const settingsPath = settingsShortcutPath(event, resolvedOrg, isOnCodesmithPage);
      if (settingsPath) {
        event.preventDefault();
        // navigate (not a bare router.push) so an already-open palette closes
        // instead of overlaying the settings page.
        navigate(settingsPath);
        return;
      }

      // Don't trigger other shortcuts if user is typing in an input/textarea (except for Escape key)
      if (event.key !== "Escape" && event.target instanceof HTMLElement && (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA" || event.target.isContentEditable)) {
        return;
      }

      // Handle Escape key when command palette is open
      if (event.key === "Escape" && open) {
        event.preventDefault();
        if (search) {
          // Clear search if there's text
          setSearch("");
        } else {
          // Close dialog if search is empty
          setOpen(false);
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOnCodesmithPage, navigate, open, resolvedOrg, search]);

  // Helper to truncate text
  const truncate = (text: string, length: number) => {
    if (text.length <= length) return text;
    return text.substring(0, length) + "...";
  };

  // Shared renderer for a Codesmith session row (used by both search
  // results and the empty-search "Recent Sessions" group)
  const renderSessionItem = (session: SearchedSession) => <CommandItem onSelect={() => {
    const url = currentOrg ? `/${currentOrg}/sessions/${session.id}` : `/sessions/${session.id}`;
    navigate(url);
  }} className="flex items-center justify-between cursor-pointer">
      <div className="flex items-center min-w-0">
        <MessageSquare className="mr-2 h-4 w-4 shrink-0" />
        <span className="truncate">
          {truncate(session.title || "Untitled", 50)}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0 ml-2">
        {session.managed_prs && session.managed_prs.length > 0 && <div className="flex items-center gap-1">
            {(() => {
          const counts = {
            open: 0,
            draft: 0,
            merged: 0,
            closed: 0
          };
          for (const pr of session.managed_prs) {
            counts[pr.status]++;
          }
          const badges: React.ReactNode[] = [];
          if (counts.open > 0) badges.push(<span key="open" className="inline-flex items-center gap-0.5 text-ds-green text-xs">
                    <GitPullRequest className="h-3 w-3" />
                    {counts.open}
                  </span>);
          if (counts.merged > 0) badges.push(<span key="merged" className="inline-flex items-center gap-0.5 text-ds-purple text-xs">
                    <GitMerge className="h-3 w-3" />
                    {counts.merged}
                  </span>);
          if (counts.draft > 0) badges.push(<span key="draft" className="inline-flex items-center gap-0.5 text-neutral-500 text-xs">
                    <GitPullRequestDraft className="h-3 w-3" />
                    {counts.draft}
                  </span>);
          if (counts.closed > 0) badges.push(<span key="closed" className="inline-flex items-center gap-0.5 text-ds-red text-xs">
                    <GitPullRequestClosed className="h-3 w-3" />
                    {counts.closed}
                  </span>);
          return badges;
        })()}
          </div>}
        <span className="text-xs text-neutral-400 dark:text-neutral-500 whitespace-nowrap">
          {formatDistanceToNow(new Date(session.last_message_at || session.updated_at)).replace(/^(about|almost|over|less than)\s+/i, "")}
        </span>
      </div>
    </CommandItem>;

  // Get dynamic search results based on user input
  const searchResults = useMemo(() => {
    // Return null only if search is completely empty
    // For 1 character, we still show "Search logs for" option
    if (!search || search.length === 0) return null;
    const lowerSearch = search.toLowerCase();

    // Count all matching workflows (only if 2+ chars)
    const matchingWorkflows = search.length >= 2 ? workflows.filter(workflow => {
      const matches = workflow.toLowerCase().includes(lowerSearch);
      return matches;
    }) : [];

    // Count all matching jobs (only if 2+ chars)
    const matchingJobs = search.length >= 2 ? jobs.filter(job => {
      const jobMatches = job.job_name.toLowerCase().includes(lowerSearch);
      const workflowMatches = job.workflow_name.toLowerCase().includes(lowerSearch);
      return jobMatches || workflowMatches;
    }) : [];
    const matchingSessions = searchSessions(search, cachedSessions, undefined, {
      userId: user?.id
    });
    return {
      workflowCount: matchingWorkflows.length,
      jobCount: matchingJobs.length,
      matchingWorkflows,
      matchingJobs,
      matchingSessions,
      hasSearch: true // Always true when we have any search text
    };
  }, [search, workflows, jobs, cachedSessions, user]);

  // Most recently active Codesmith sessions, surfaced at the top of the
  // palette when it opens on a Codesmith page with no search text.
  const recentSessions = useMemo(() => {
    if (!isOnSessionPage) return [];
    return getRecentSessions(cachedSessions, 3, {
      userId: user?.id
    });
  }, [isOnSessionPage, cachedSessions, user]);
  enum CommandSection {
    QUICK_ACTIONS = "Quick Actions",
    RECENT_SESSIONS = "Recent Sessions",
    MY_SESSIONS = "My Sessions",
    TEAM_SESSIONS = "Team Sessions",
    NAVIGATION = "Navigation",
    TIME_RANGE = "Time Range",
    ACTIONS = "Actions",
    HELP = "Help",
  }
  type NavigationItem = {
    name: string;
    icon: React.ReactElement;
    shortcut?: string;
    onSelect: () => void;
    priority?: number;
  };
  type NavigationGroup = {
    group: string;
    items: NavigationItem[];
    priority?: number;
  };
  type SearchResultItem = {
    key: string;
    element: React.ReactElement;
    priority: number;
    section: CommandSection;
  };

  // Calculate priority for navigation items based on search
  const getNavigationItemPriority = (itemName: string): number => getNavigationItemPriorityFor(itemName, search);

  // Special priority function for "Switch organization" - requires 3 characters to match
  const getSwitchOrgPriority = (): number => {
    if (search.length >= 3 && "switch organization".toLowerCase().includes(search.toLowerCase())) {
      return 10; // Show it when user types 3+ characters that match
    }
    return -1; // Hide it by default
  };

  // Priority function for Actions section items
  const getActionItemPriority = (itemName_0: string): number => getActionItemPriorityFor(itemName_0, search);

  // Priority function for time period items
  const getTimePeriodPriority = (itemName_1: string): number => {
    // Only show time range options on specific pages
    const isOnRelevantPage = router.pathname.includes("/runs/workflows") || router.pathname.includes("/runs/jobs") || router.pathname.includes("/logs");
    if (!isOnRelevantPage) {
      return -1; // Hide on other pages
    }
    const basePriority = 1;
    const numberBoostPriority = 30;

    // Check if search is a number and the item name contains that number
    const searchNumber = parseInt(search, 10);
    if (!isNaN(searchNumber) && search.trim() === searchNumber.toString()) {
      // Only boost if the item name contains the searched number
      if (itemName_1.toLowerCase().includes(search)) {
        return numberBoostPriority;
      }
      // Don't boost items that don't match the number
      return basePriority;
    }

    // Also boost if the item name matches the search text
    if (search.length > 0 && itemName_1.toLowerCase().includes(search.toLowerCase())) {
      return numberBoostPriority;
    }
    return basePriority;
  };
  const navigationItems: NavigationGroup[] = [{
    group: "Navigation",
    items: [{
      name: "Jobs",
      icon: <CirclePlay className="mr-2 h-4 w-4" />,
      shortcut: "G J",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/runs/jobs` : "/runs/jobs"),
      get priority() {
        return getNavigationItemPriority("Jobs");
      }
    }, {
      name: "Workflows",
      icon: <Workflow className="mr-2 h-4 w-4" />,
      shortcut: "G W",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/runs/workflows` : "/runs/workflows"),
      get priority() {
        return getNavigationItemPriority("Workflows");
      }
    }, {
      name: "Logs",
      icon: <Terminal className="mr-2 h-4 w-4" />,
      shortcut: "G L",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/logs` : "/logs"),
      get priority() {
        return getNavigationItemPriority("Logs");
      }
    }, {
      name: "Actions Analytics",
      icon: <GaugeCircleIcon className="mr-2 h-4 w-4" />,
      shortcut: "G A",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/analytics` : "/analytics"),
      get priority() {
        return getNavigationItemPriority("Actions Analytics");
      }
    }, {
      name: "Docker Analytics",
      icon: <SiDocker className="mr-2 h-4 w-4" />,
      shortcut: "G D",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/docker-analytics` : "/docker-analytics"),
      get priority() {
        return getNavigationItemPriority("Docker Analytics");
      }
    }, {
      name: "Cache",
      icon: <DatabaseZap className="mr-2 h-4 w-4" />,
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/cache` : "/cache"),
      get priority() {
        return getNavigationItemPriority("Cache");
      }
    }, {
      name: "Sticky Disks",
      icon: <HardDrive className="mr-2 h-4 w-4" />,
      shortcut: "G S",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/stickydisks` : "/stickydisks"),
      get priority() {
        return getNavigationItemPriority("Sticky Disks");
      }
    }, {
      name: "Monitors",
      icon: <Activity className="mr-2 h-4 w-4" />,
      shortcut: "G M",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/monitors` : "/monitors"),
      get priority() {
        return getNavigationItemPriority("Monitors");
      }
    }, {
      name: "Rightsize Runners",
      icon: <Ruler className="mr-2 h-4 w-4" />,
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/rightsize` : "/rightsize"),
      get priority() {
        return getNavigationItemPriority("Rightsize Runners");
      }
    },
    // Only surface Automations when the Codesmith automations feature is
    // enabled for the active org (mirrors the sidebar nav gating). Gated by
    // omission rather than a negative priority because nav items are added
    // with `item.priority || 0`, which would coerce -1 back to 0.
    ...(codesmithAutomationsEnabled ? [{
      name: "Automations",
      icon: <CalendarClock className="mr-2 h-4 w-4" />,
      onSelect: () => navigate(resolvedOrg ? `/${resolvedOrg}/automations` : "/automations"),
      get priority() {
        return getNavigationItemPriority("Automations");
      }
    }] : []), ...(codesmithEnabled ? [{
      name: "[code]smith Usage",
      icon: <CodesmithIcon className="mr-2 size-4" />,
      onSelect: () => navigate(codesmithUsagePath(resolvedOrg)),
      get priority() {
        return getNavigationItemPriority("[code]smith Usage");
      }
    }] : []), {
      name: "GitHub Actions Usage",
      icon: <GithubActionsIcon className="mr-2 size-4" />,
      onSelect: () => navigate(githubActionsUsagePath(resolvedOrg)),
      get priority() {
        return getNavigationItemPriority("GitHub Actions Usage");
      }
    }, {
      name: "Usage & Billing",
      icon: <BarChart3 className="mr-2 h-4 w-4" />,
      shortcut: "G U",
      onSelect: () => navigate(currentOrg ? `/${currentOrg}/usage` : "/usage"),
      get priority() {
        return getNavigationItemPriority("Usage & Billing");
      }
    }, {
      name: "Settings",
      icon: <Settings className="mr-2 h-4 w-4" />,
      shortcut: isMac ? "⌘," : "Ctrl+,",
      // Mirrors the keyboard shortcut it advertises, including the
      // Codesmith tab targeting on Codesmith pages.
      onSelect: () => navigate(settingsPagePath(resolvedOrg, isOnCodesmithPage)),
      get priority() {
        return getNavigationItemPriority("Settings");
      }
    }, {
      name: "Switch organization",
      icon: <Building2 className="mr-2 h-4 w-4" />,
      onSelect: () => {
        // Dispatch custom event to open org switcher
        window.dispatchEvent(new CustomEvent("openOrgSwitcher"));
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getSwitchOrgPriority();
      }
    }]
  }, {
    group: "Actions",
    items: [{
      name: "Open MCP settings",
      icon: <Settings className="mr-2 h-4 w-4" />,
      onSelect: () => {
        navigate(`${settingsPath(currentOrg, "codesmith", "mcp")}#mcp-servers`);
        // router.push uses history.pushState, which does NOT fire the
        // browser's hashchange event. If the user is already on the MCP
        // settings page, neither the pathname nor hashchange updates, so
        // the scroll-to-hash listener in the settings layout would never
        // run. Dispatch a synthetic hashchange so that listener handles
        // every entry path uniformly. Harmless when the layout hasn't
        // mounted yet (no listener to receive it).
        if (typeof window !== "undefined") {
          setTimeout(() => {
            window.dispatchEvent(new Event("hashchange"));
          }, 0);
        }
      },
      get priority() {
        return getActionItemPriority("Open MCP settings");
      }
    }, {
      name: "Copy prompt for AI",
      icon: <Sparkles className="mr-2 h-4 w-4" />,
      onSelect: () => {
        window.dispatchEvent(new CustomEvent("copyTestAIPrompt"));
        setOpen(false);
        setSearch("");
      },
      get priority() {
        const isTestsPage = router.query.tab === "tests" && router.pathname.includes("/jobs/");
        // Return -1 to hide if not on tests page, otherwise use action item priority
        return isTestsPage ? getActionItemPriority("Copy prompt for AI") : -1;
      }
    }, ...[{
      key: "light",
      name: "Change theme to light mode",
      icon: <Sun className="mr-2 h-4 w-4" />
    }, {
      key: "dark",
      name: "Change theme to dark mode",
      icon: <Moon className="mr-2 h-4 w-4" />
    }, {
      key: "system",
      name: "Change theme to system mode",
      icon: <Monitor className="mr-2 h-4 w-4" />
    }].filter(opt => opt.key !== theme).map(opt_0 => ({
      name: opt_0.name,
      icon: opt_0.icon,
      onSelect: () => {
        setTheme(opt_0.key);
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getActionItemPriority(opt_0.name);
      }
    }))]
  }, {
    group: "Time Range",
    items: [{
      name: "Last 1 hour",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges = labelsToDateRanges();
        setRecentTimeTab("1h");
        setRecentDateRange(dateRanges["1h"]);
        updateDateRangeUrl("1h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 1 hour");
      }
    }, {
      name: "Last 2 hours",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_0 = labelsToDateRanges();
        setRecentTimeTab("2h");
        setRecentDateRange(dateRanges_0["2h"]);
        updateDateRangeUrl("2h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 2 hours");
      }
    }, {
      name: "Last 4 hours",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_1 = labelsToDateRanges();
        setRecentTimeTab("4h");
        setRecentDateRange(dateRanges_1["4h"]);
        updateDateRangeUrl("4h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 4 hours");
      }
    }, {
      name: "Last 12 hours",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_2 = labelsToDateRanges();
        setRecentTimeTab("12h");
        setRecentDateRange(dateRanges_2["12h"]);
        updateDateRangeUrl("12h");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 12 hours");
      }
    }, {
      name: "Last 1 day",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_3 = labelsToDateRanges();
        setRecentTimeTab("1D");
        setRecentDateRange(dateRanges_3["1D"]);
        updateDateRangeUrl("1D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 1 day");
      }
    }, {
      name: "Last 2 days",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_4 = labelsToDateRanges();
        setRecentTimeTab("2D");
        setRecentDateRange(dateRanges_4["2D"]);
        updateDateRangeUrl("2D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 2 days");
      }
    }, {
      name: "Last 5 days",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_5 = labelsToDateRanges();
        setRecentTimeTab("5D");
        setRecentDateRange(dateRanges_5["5D"]);
        updateDateRangeUrl("5D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 5 days");
      }
    }, {
      name: "Last 14 days",
      icon: <Clock className="mr-2 h-4 w-4" />,
      onSelect: () => {
        const dateRanges_6 = labelsToDateRanges();
        setRecentTimeTab("14D");
        setRecentDateRange(dateRanges_6["14D"]);
        updateDateRangeUrl("14D");
        setOpen(false);
        setSearch("");
      },
      get priority() {
        return getTimePeriodPriority("Last 14 days");
      }
    }]
  }, {
    group: "Help",
    items: [{
      name: "View documentation",
      icon: <FileText className="mr-2 h-4 w-4" />,
      onSelect: () => {
        window.open("https://docs.blacksmith.sh", "_blank");
        setOpen(false);
      },
      priority: 0
    }, {
      name: "Get support",
      icon: <HelpCircle className="mr-2 h-4 w-4" />,
      onSelect: () => {
        navigate(currentOrg ? `/${currentOrg}/support` : "/FastActions/support");
      },
      priority: 0
    }]
  }];
  return <DialogPrimitive.Root open={open} onOpenChange={newOpen => {
    // Only allow closing if there's no search text
    if (newOpen === false && search) {
      // Don't close, just clear search
      setSearch("");
      return;
    }
    setOpen(newOpen);
  }} modal={true}>
      <CommandDialogContent onPointerDownOutside={e => {
      // Prevent closing on click outside if there's search text
      if (search) {
        e.preventDefault();
        setSearch("");
      }
    }} onInteractOutside={e_0 => {
      // Prevent closing on interaction outside if there's search text
      if (search) {
        e_0.preventDefault();
        setSearch("");
      }
    }} onEscapeKeyDown={e_1 => {
      // Always prevent default and handle manually
      e_1.preventDefault();
      e_1.stopPropagation();
      if (search) {
        setSearch("");
      } else {
        setOpen(false);
      }
    }}>
        <Command className="rounded-lg" shouldFilter={false}>
          <CommandInput placeholder="Type a command or search..." value={search} onValueChange={value => {
          setSearch(value);
        }} autoFocus />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            {(() => {
            // Create all possible items with priorities
            const allItems: SearchResultItem[] = [];

            // Add search-specific results when searching
            if (search && searchResults) {
              // Workflow matches - priority 50, but 1 if more than 20 matches
              if (search.length >= 2 && searchResults.workflowCount > 0) {
                allItems.push({
                  key: "search-workflows",
                  priority: searchResults.workflowCount > 20 ? 1 : 50,
                  section: CommandSection.QUICK_ACTIONS,
                  element: <CommandItem onSelect={() => {
                    const params = new URLSearchParams();
                    searchResults.matchingWorkflows.forEach(workflow_0 => {
                      params.append("workflow", workflow_0);
                    });
                    const url_0 = currentOrg ? `/${currentOrg}/runs/workflows?${params.toString()}` : `/runs/workflows?${params.toString()}`;
                    navigate(url_0);
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <Workflow className="mr-2 h-4 w-4" />
                          <span>
                            View {searchResults.workflowCount} matching{" "}
                            {searchResults.workflowCount === 1 ? "workflow" : "workflows"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Show first match */}
                          <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingWorkflows[0]}>
                            {truncate(searchResults.matchingWorkflows[0], 20)}
                          </Badge>

                          {/* Show second match if exists */}
                          {searchResults.workflowCount > 1 && <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingWorkflows[1]}>
                              {truncate(searchResults.matchingWorkflows[1], 20)}
                            </Badge>}

                          {/* Show more badge with tooltip if > 2 matches */}
                          {searchResults.workflowCount > 2 && <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 cursor-help">
                                    {searchResults.workflowCount - 2} more...
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-md">
                                  <div className="flex flex-wrap gap-1">
                                    {searchResults.matchingWorkflows.slice(2).map(workflow_1 => <Badge key={workflow_1} variant="secondary" className="text-xs" title={workflow_1}>
                                          {truncate(workflow_1, 30)}
                                        </Badge>)}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>}
                        </div>
                      </CommandItem>
                });
              }

              // Job matches - priority 50, but 1 if more than 20 matches
              if (search.length >= 2 && searchResults.jobCount > 0) {
                allItems.push({
                  key: "search-jobs",
                  priority: searchResults.jobCount > 20 ? 1 : 50,
                  section: CommandSection.QUICK_ACTIONS,
                  element: <CommandItem onSelect={() => {
                    const jobExpressions = searchResults.matchingJobs.map(job_0 => `${job_0.workflow_name}::${job_0.job_name}`);
                    const orExpression = `(${jobExpressions.join(" OR ")})`;
                    const params_0 = new URLSearchParams();
                    params_0.set("job", orExpression);
                    const url_1 = currentOrg ? `/${currentOrg}/runs/jobs?${params_0.toString()}` : `/runs/jobs?${params_0.toString()}`;
                    navigate(url_1);
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <CirclePlay className="mr-2 h-4 w-4" />
                          <span>
                            View {searchResults.jobCount} matching{" "}
                            {searchResults.jobCount === 1 ? "job" : "jobs"}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Show first match */}
                          <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingJobs[0].job_name}>
                            {truncate(searchResults.matchingJobs[0].job_name, 20)}
                          </Badge>

                          {/* Show second match if exists */}
                          {searchResults.jobCount > 1 && <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400" title={searchResults.matchingJobs[1].job_name}>
                              {truncate(searchResults.matchingJobs[1].job_name, 20)}
                            </Badge>}

                          {/* Show more badge with tooltip if > 2 matches */}
                          {searchResults.jobCount > 2 && <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge variant="secondary" className="text-xs bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 cursor-help">
                                    {searchResults.jobCount - 2} more...
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="left" className="max-w-md">
                                  <div className="flex flex-wrap gap-1">
                                    {searchResults.matchingJobs.slice(2).map(job_1 => <Badge key={`${job_1.workflow_name}:${job_1.job_name}`} variant="secondary" className="text-xs" title={job_1.job_name}>
                                          {truncate(job_1.job_name, 30)}
                                        </Badge>)}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>}
                        </div>
                      </CommandItem>
                });
              }

              // Search logs - priority 40, but lower if workflows/jobs have poor matches
              const hasGoodWorkflowJobMatches = searchResults.workflowCount > 0 && searchResults.workflowCount <= 20 || searchResults.jobCount > 0 && searchResults.jobCount <= 20;
              allItems.push({
                key: "search-logs",
                priority: hasGoodWorkflowJobMatches ? 40 : 10,
                section: CommandSection.QUICK_ACTIONS,
                element: <CommandItem onSelect={() => {
                  // If search contains spaces, wrap in quotes for exact phrase search
                  const searchQuery = search.includes(" ") ? `"${search}"` : search;
                  const url_2 = currentOrg ? `/${currentOrg}/logs?q=${encodeURIComponent(searchQuery)}` : `/logs?q=${encodeURIComponent(searchQuery)}`;
                  navigate(url_2);
                }} className="flex items-center justify-between cursor-pointer">
                      <div className="flex items-center">
                        <Search className="mr-2 h-4 w-4" />
                        <span>Search logs for &ldquo;{search}&rdquo;</span>
                      </div>
                    </CommandItem>
              });
            }

            // Codesmith chat matches - split into My Sessions and Team Sessions
            if (search && searchResults && searchResults.matchingSessions.length > 0) {
              const mySessionPriority = isOnSessionPage ? 100 : 3;
              const teamSessionPriority = isOnSessionPage ? 99 : 2;
              searchResults.matchingSessions.forEach(session_0 => {
                const section = session_0.isMine ? CommandSection.MY_SESSIONS : CommandSection.TEAM_SESSIONS;
                const priority = session_0.isMine ? mySessionPriority : teamSessionPriority;
                allItems.push({
                  key: `session-${session_0.id}`,
                  priority,
                  section,
                  element: renderSessionItem(session_0)
                });
              });
            }

            // Semantic transcript matches from the server, deduped against
            // the client-side title-substring matches above and ranked just
            // below them within the same sections.
            if (search && semanticSessions.length > 0) {
              const titleMatchIds = new Set((searchResults?.matchingSessions ?? []).map(session_1 => session_1.id));
              const mySemanticPriority = isOnSessionPage ? 98 : 3;
              const teamSemanticPriority = isOnSessionPage ? 97 : 2;
              semanticSessions.filter(session_2 => !titleMatchIds.has(session_2.id)).forEach(session_3 => {
                const isMine = user?.id != null && session_3.user_id === user.id;
                allItems.push({
                  key: `session-${session_3.id}`,
                  priority: isMine ? mySemanticPriority : teamSemanticPriority,
                  section: isMine ? CommandSection.MY_SESSIONS : CommandSection.TEAM_SESSIONS,
                  element: renderSessionItem({
                    ...session_3,
                    isMine
                  })
                });
              });
            }

            // Recent Codesmith sessions shown at the top when the palette
            // opens on a Codesmith page with no search text
            if (!search && recentSessions.length > 0) {
              recentSessions.forEach(session_4 => {
                allItems.push({
                  key: `recent-session-${session_4.id}`,
                  priority: 100,
                  section: CommandSection.RECENT_SESSIONS,
                  element: renderSessionItem(session_4)
                });
              });
            }

            // Dynamic time period options when typing a number
            // Only show on relevant pages
            const isOnRelevantPage_0 = router.pathname.includes("/runs/workflows") || router.pathname.includes("/runs/jobs") || router.pathname.includes("/logs");
            const searchNumber_0 = parseInt(search, 10);
            if (isOnRelevantPage_0 && !isNaN(searchNumber_0) && search.trim() === searchNumber_0.toString() && searchNumber_0 >= 1 && searchNumber_0 <= 32) {
              const dateRanges_7 = labelsToDateRanges();

              // Define preset values to avoid duplicates
              const presetHours = [1, 2, 4, 12];
              const presetDays = [1, 2, 5, 14];

              // Add "last X hours" option only if X <= 24 and not a preset
              if (searchNumber_0 <= 24 && !presetHours.includes(searchNumber_0)) {
                const hourKey = `${searchNumber_0}h`;
                allItems.push({
                  key: `dynamic-hours-${searchNumber_0}`,
                  priority: 35,
                  section: CommandSection.TIME_RANGE,
                  element: <CommandItem onSelect={() => {
                    // Check if this time period already exists in labelsToDateRanges
                    if (dateRanges_7[hourKey]) {
                      setRecentTimeTab(hourKey);
                      setRecentDateRange(dateRanges_7[hourKey]);
                      updateDateRangeUrl(hourKey);
                    } else {
                      // Create a custom date range
                      const today = new Date();
                      const customRange = {
                        from: new Date(today.getTime() - searchNumber_0 * 60 * 60 * 1000),
                        to: today
                      };
                      setRecentTimeTab(hourKey);
                      setRecentDateRange(customRange);
                      updateDateRangeUrl(hourKey, customRange);
                    }
                    setOpen(false);
                    setSearch("");
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4" />
                          <span>
                            Last {searchNumber_0}{" "}
                            {searchNumber_0 === 1 ? "hour" : "hours"}
                          </span>
                        </div>
                      </CommandItem>
                });
              }

              // Add "last X days" option only if not a preset
              if (!presetDays.includes(searchNumber_0)) {
                const dayKey = `${searchNumber_0}D`;
                allItems.push({
                  key: `dynamic-days-${searchNumber_0}`,
                  priority: 35,
                  section: CommandSection.TIME_RANGE,
                  element: <CommandItem onSelect={() => {
                    // Check if this time period already exists in labelsToDateRanges
                    if (dateRanges_7[dayKey]) {
                      setRecentTimeTab(dayKey);
                      setRecentDateRange(dateRanges_7[dayKey]);
                      updateDateRangeUrl(dayKey);
                    } else {
                      // Create a custom date range
                      const today_0 = new Date();
                      const customRange_0 = {
                        from: new Date(today_0.getTime() - searchNumber_0 * 24 * 60 * 60 * 1000),
                        to: today_0
                      };
                      setRecentTimeTab(dayKey);
                      setRecentDateRange(customRange_0);
                      updateDateRangeUrl(dayKey, customRange_0);
                    }
                    setOpen(false);
                    setSearch("");
                  }} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4" />
                          <span>
                            Last {searchNumber_0}{" "}
                            {searchNumber_0 === 1 ? "day" : "days"}
                          </span>
                        </div>
                      </CommandItem>
                });
              }
            }

            // Add navigation items (filtered when searching)
            navigationItems.forEach(section_0 => {
              const filteredItems = search ? section_0.items.filter(item => navigationItemMatchesSearch(item.name, search)) : section_0.items;
              filteredItems.forEach(item_0 => {
                const itemSection = section_0.group === "Help" ? CommandSection.HELP : section_0.group === "Actions" ? CommandSection.ACTIONS : section_0.group === "Time Range" ? CommandSection.TIME_RANGE : CommandSection.NAVIGATION;
                allItems.push({
                  key: `nav-${item_0.name}`,
                  priority: item_0.priority || 0,
                  section: itemSection,
                  element: <CommandItem key={item_0.name} onSelect={item_0.onSelect} className="flex items-center justify-between cursor-pointer">
                        <div className="flex items-center">
                          {item_0.icon}
                          <span>{item_0.name}</span>
                        </div>
                        {item_0.shortcut && <CommandShortcut className="text-neutral-400 dark:text-neutral-500">
                            {item_0.shortcut}
                          </CommandShortcut>}
                      </CommandItem>
                });
              });
            });

            // Filter out items with negative priority (not relevant)
            const relevantItems = allItems.filter(item_1 => item_1.priority >= 0);

            // Group items by section first
            const groupedBySection = relevantItems.reduce((acc, item_2) => {
              if (!acc[item_2.section]) {
                acc[item_2.section] = [];
              }
              acc[item_2.section].push(item_2);
              return acc;
            }, {} as Record<CommandSection, SearchResultItem[]>);

            // Calculate section priority (max priority of items in section) and sort sections
            const sectionsWithPriority = Object.keys(groupedBySection).map(sectionKey => {
              const section_1 = sectionKey as CommandSection;
              const items = groupedBySection[section_1];
              const maxPriority = Math.max(...items.map(item_3 => item_3.priority));

              // Sort items within section by priority (descending), then maintain original order
              const sortedItems = items.sort((a, b) => b.priority - a.priority);
              return {
                section: section_1,
                maxPriority,
                items: sortedItems
              };
            }).filter(sectionData => sectionData.maxPriority >= 0) // Filter out sections with negative max priority
            .sort((a_0, b_0) => b_0.maxPriority - a_0.maxPriority); // Sort sections by max priority (descending)

            return sectionsWithPriority.map((sectionData_0, index) => {
              const isLastSection = index === sectionsWithPriority.length - 1;
              return <React.Fragment key={sectionData_0.section}>
                    <CommandGroup heading={sectionData_0.section}>
                      {sectionData_0.items.map(item_4 => <React.Fragment key={item_4.key}>
                          {item_4.element}
                        </React.Fragment>)}
                    </CommandGroup>
                    {!isLastSection && <CommandSeparator className="my-2" />}
                  </React.Fragment>;
            });
          })()}
          </CommandList>
          <div className="border-t border-[var(--color-border)] dark:border-[var(--color-border)] px-4 py-2 text-xs text-neutral-500 dark:text-neutral-400 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShortcutKeys keys={["↑", "↓"]} />
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-2">
              <ShortcutKeys keys={["⏎"]} />
              <span>Select</span>
            </div>
            <div className="flex items-center gap-2">
              <ShortcutKeys keys={["esc"]} />
              <span>{search ? "Clear" : "Close"}</span>
            </div>
          </div>
        </Command>
      </CommandDialogContent>
    </DialogPrimitive.Root>;
}